/**
 * Build a PickHistoryEntry from a group challenge for "me vs selected opponent" view.
 * Match list follows arena: use challenge.matchIds (canonical list) so every match gets a row and picks align.
 * Match data and picks come from challenge.picks; points totals from all picks so winner stays correct.
 */

import type { ChallengeWithStats } from '../../../types/challenges'
import type { ChallengePick } from '../../../types/challenges'
import type { PickHistoryEntry, MatchBet, MatchPick } from '../types/friends.types'
import { MATCH_WINNER_MARKET_ID } from '../../challenges/constants/challenges.constants'
import { toISOStringSafe } from '../../../helpers/dateFormatters.helper'
import { buildMatchByIdFromPicks } from '../../challenges/helpers/buildMatchByIdFromPicks.helper'
import {
  CHALLENGE_STATUS,
  API_STATUS_TO_CHALLENGE_STATUS,
  BET_HISTORY_DISPLAY_FALLBACKS,
} from '../constants/challengeStatus.constants'
import { getLeagueNameFromMatchPayload } from './getLeagueNameFromMatchPayload.helper'

const EMPTY_PREDICTION = {
  outcome: null as 'home' | 'away' | 'draw' | null,
  pointsEarned: 0,
}

function buildMatchStub(_matchId: string): MatchBet['matchResult'] {
  return {
    homeScore: null,
    awayScore: null,
    halfTimeHomeScore: null,
    halfTimeAwayScore: null,
    status: BET_HISTORY_DISPLAY_FALLBACKS.UNKNOWN,
  }
}

function buildMatchResult(match: unknown): MatchBet['matchResult'] {
  if (!match || typeof match !== 'object') {
    return buildMatchStub('')
  }
  const m = match as Record<string, unknown>
  return {
    homeScore: (m.homeScore as number | null) ?? null,
    awayScore: (m.awayScore as number | null) ?? null,
    halfTimeHomeScore: (m.halfTimeHomeScore as number | null) ?? null,
    halfTimeAwayScore: (m.halfTimeAwayScore as number | null) ?? null,
    status: (m.status as string) ?? BET_HISTORY_DISPLAY_FALLBACKS.UNKNOWN,
  }
}

function getTeamPayload(match: unknown, side: 'homeTeam' | 'awayTeam'): MatchBet['homeTeam'] {
  if (!match || typeof match !== 'object') return BET_HISTORY_DISPLAY_FALLBACKS.UNKNOWN
  const m = match as Record<string, unknown>
  const team = m[side]
  if (team && typeof team === 'object' && team !== null && 'name' in team) {
    const t = team as { name: string; kitPrimary?: string | null; kitSecondary?: string | null; kitType?: string | null }
    return {
      name: t.name,
      ...(t.kitPrimary != null && { kitPrimary: t.kitPrimary }),
      ...(t.kitSecondary != null && { kitSecondary: t.kitSecondary }),
      ...(t.kitType != null && { kitType: t.kitType }),
    }
  }
  if (typeof team === 'string') return team
  return BET_HISTORY_DISPLAY_FALLBACKS.UNKNOWN
}

function getOdds(match: unknown): MatchBet['odds'] {
  if (!match || typeof match !== 'object') return null
  const m = match as Record<string, unknown>
  const odds = m.odds
  if (odds && typeof odds === 'object' && odds !== null && 'home' in odds) {
    const o = odds as { home?: number; draw?: number | null; away?: number }
    return {
      home: o.home ?? 0,
      draw: o.draw ?? null,
      away: o.away ?? 0,
    }
  }
  return null
}

function toMatchPick(pick: ChallengePick): MatchPick {
  return {
    marketId: Number(pick.marketId),
    marketName: (pick.marketName && String(pick.marketName).trim()) || 'Market',
    value: pick.value ?? '',
    odd: Number(pick.odd) || 0,
    pointsEarned: pick.pointsEarned ?? null,
    isCorrectOutcome: pick.isCorrectOutcome ?? null,
    isExactScore: pick.isExactScore ?? null,
  }
}

function parseOutcome(value: string | undefined): 'home' | 'away' | 'draw' | null {
  const v = value ? String(value).toLowerCase() : ''
  if (v === 'home' || v === 'away' || v === 'draw') return v
  return null
}

export interface GroupChallengeToPickHistoryEntryOptions {
  /** When true, log a dev warning when pick.match is missing. */
  logMissingMatch?: boolean
}

function buildMatchBet(
  matchId: string,
  myPicksForMatch: ChallengePick[],
  theirPicksForMatch: ChallengePick[],
  matchData: unknown,
  challengeId: string,
  options?: GroupChallengeToPickHistoryEntryOptions
): MatchBet {
  if (!matchData && (myPicksForMatch.length > 0 || theirPicksForMatch.length > 0)) {
    if (typeof __DEV__ !== 'undefined' && __DEV__ && options?.logMissingMatch !== false) {
      // eslint-disable-next-line no-console
      console.warn('[groupChallengeToPickHistoryEntry] Missing pick.match for matchId', {
        challengeId,
        matchId,
      })
    }
  }

  const matchResult = matchData ? buildMatchResult(matchData) : buildMatchStub(matchId)
  const homeTeam = matchData ? getTeamPayload(matchData, 'homeTeam') : BET_HISTORY_DISPLAY_FALLBACKS.UNKNOWN
  const awayTeam = matchData ? getTeamPayload(matchData, 'awayTeam') : BET_HISTORY_DISPLAY_FALLBACKS.UNKNOWN
  const leagueName = matchData ? getLeagueNameFromMatchPayload(matchData) : BET_HISTORY_DISPLAY_FALLBACKS.UNKNOWN
  const dateStr =
    matchData && typeof matchData === 'object' && matchData !== null && 'date' in matchData
      ? toISOStringSafe((matchData as Record<string, unknown>).date as Date | string | undefined)
      : ''

  const user1x2 = myPicksForMatch.find((p) => Number(p.marketId) === MATCH_WINNER_MARKET_ID)
  const friend1x2 = theirPicksForMatch.find((p) => Number(p.marketId) === MATCH_WINNER_MARKET_ID)

  const userPrediction = user1x2
    ? { outcome: parseOutcome(user1x2.value), pointsEarned: user1x2.pointsEarned ?? 0 }
    : EMPTY_PREDICTION

  const friendPrediction = friend1x2
    ? { outcome: parseOutcome(friend1x2.value), pointsEarned: friend1x2.pointsEarned ?? 0 }
    : EMPTY_PREDICTION

  const userPicks1x2 = user1x2 ? [toMatchPick(user1x2)] : []
  const friendPicks1x2 = friend1x2 ? [toMatchPick(friend1x2)] : []
  const userPicksOther = myPicksForMatch
    .filter((p) => Number(p.marketId) !== MATCH_WINNER_MARKET_ID)
    .map(toMatchPick)
  const friendPicksOther = theirPicksForMatch
    .filter((p) => Number(p.marketId) !== MATCH_WINNER_MARKET_ID)
    .map(toMatchPick)

  const odds = matchData ? getOdds(matchData) : null

  return {
    matchId,
    homeTeam,
    awayTeam,
    date: dateStr,
    league: leagueName,
    odds,
    userPrediction,
    friendPrediction,
    matchResult,
    userPicks: [...userPicks1x2, ...userPicksOther],
    friendPicks: [...friendPicks1x2, ...friendPicksOther],
  }
}

/**
 * Build a PickHistoryEntry from a group challenge for the current user vs selected opponent.
 * - Union of matches: all matchIds that either user or opponent have a pick for.
 * - Points totals: sum of pointsEarned from ALL picks per user (so winner is correct even if some match data is missing).
 * - Missing pick.match: stub row is used; points still counted; optional dev warning.
 */
export function groupChallengeToPickHistoryEntry(
  challenge: ChallengeWithStats,
  currentUserId: string,
  selectedOpponentId: string | null,
  options?: GroupChallengeToPickHistoryEntryOptions
): PickHistoryEntry | null {
  if (!selectedOpponentId) return null

  const norm = (id: string | undefined | null) => String(id ?? '').trim()
  const currentNorm = norm(currentUserId)
  const opponentNorm = norm(selectedOpponentId)
  const picks = challenge.picks ?? []
  const myPicks = picks.filter((p) => norm(p.userId) === currentNorm)
  const theirPicks = picks.filter((p) => norm(p.userId) === opponentNorm)

  const userPoints = myPicks.reduce((sum, p) => sum + (p.pointsEarned ?? 0), 0)
  const friendPoints = theirPicks.reduce((sum, p) => sum + (p.pointsEarned ?? 0), 0)
  const viewerDidNotParticipate = myPicks.length === 0

  const statusKey = (challenge.status?.toLowerCase?.() ?? '') as string
  const status: PickHistoryEntry['status'] =
    API_STATUS_TO_CHALLENGE_STATUS[statusKey] ?? CHALLENGE_STATUS.COMPLETED
  let winner: PickHistoryEntry['winner'] =
    status === CHALLENGE_STATUS.ACTIVE
      ? null
      : userPoints > friendPoints
        ? 'user'
        : friendPoints > userPoints
          ? 'friend'
          : 'tie'

  if (viewerDidNotParticipate && status === CHALLENGE_STATUS.COMPLETED) {
    winner = null
  }

  // Align with arena: use challenge.matchIds as canonical list (ChallengeCard does the same).
  const matchIds = (challenge.matchIds ?? [])
    .map((id) => (id != null && String(id).trim() !== '' ? String(id) : null))
    .filter((id): id is string => id !== null)

  const matchByIdFromPicks = buildMatchByIdFromPicks(picks)

  const matches: MatchBet[] = []
  for (const matchId of matchIds) {
    const myPicksForMatch = myPicks.filter((p) => p.matchId != null && String(p.matchId) === matchId)
    const theirPicksForMatch = theirPicks.filter((p) => p.matchId != null && String(p.matchId) === matchId)
    const matchData: unknown = matchByIdFromPicks[matchId] ?? null
    const pick = buildMatchBet(
      matchId,
      myPicksForMatch,
      theirPicksForMatch,
      matchData,
      challenge.id,
      options
    )
    matches.push(pick)
  }

  const date = toISOStringSafe(challenge.createdAt as Date | string | undefined)

  return {
    challengeId: challenge.id,
    challengeName: challenge.name ?? BET_HISTORY_DISPLAY_FALLBACKS.CHALLENGE_NAME,
    date,
    status,
    userPoints,
    friendPoints,
    winner,
    matches,
    stakeCoins: challenge.stakeCoins ?? null,
    participantCount: challenge.participantCount ?? null,
    creatorId: challenge.creatorId ?? null,
    viewerDidNotParticipate,
  }
}
